import React from 'react';
import { Dropdown, IDropdownOption, IDropdownStyles, ResponsiveMode } from '@fluentui/react';


interface CaseDropdownProps {
  selectedCase: string;
  casesString: string;
  caseNumbersAndNamesVal: string;
  onCaseChange: (caseNumber: string) => void;
  placeholder?: string;
  memberFirstNames: { [key: string]: string } | null;
}

const CaseDropdown: React.FC<CaseDropdownProps> = ({
  selectedCase,
  casesString,
  caseNumbersAndNamesVal,
  onCaseChange,
  placeholder = "Select Case",
  memberFirstNames
}) => {

  // Helper function to format the names (remove "and" and replace with comma)
  const formatNames = (names: string, maxLength = 50): string => {

    let formattedNames = names.replace(' and ', ', ').replace(/"/g, '');

    if (formattedNames.length > maxLength) {
      formattedNames = formattedNames.substring(0, maxLength - 3) + '...';
    }

    return formattedNames;
  };

  // Helper function to extract names for a specific case number
  const getNameForCase = (caseNumber: string): string => {
    if (!memberFirstNames) return '';
    
    // Find the matching entry in memberFirstNames
    const entry = Object.entries(memberFirstNames).find(
      ([key]) => key === caseNumber
    );
    
    if (entry) {
      return formatNames(entry[1]);
    }
    return '';
  };

  const getCaseOptions = (cases: string, casesAndNames: string): IDropdownOption[] => {
    if (casesAndNames && casesAndNames.trim().length > 0) {
      return casesAndNames
        .split(/,\s*(?=\d)/)
        .map(c => c.trim())
        .filter(Boolean)
        .sort((a, b) => Number(b.split('|')[0]) - Number(a.split('|')[0])) // Sort in descending order
        .map(caseEntry => {
          const [caseNumber, names] = caseEntry.split('|').map(s => s.trim());
          return {
            key: caseNumber,
            text: caseNumber + (names ? ` | ${names}` : '')
          };
        });
    }
    return cases
      .split(',')
      .map(c => c.trim())
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a)) // Sort in descending order
      .map(caseNumber => {
        const names = getNameForCase(caseNumber);
        return {
          key: caseNumber,
          text: caseNumber + (names ? ` | ${names}` : '')
        };
      });
  };

  
  const dropdownStyles: Partial<IDropdownStyles> = {
    root: {
      border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color)',
      borderRadius: '8px',
      margin: '10px',
      marginRight: '15px',
    },
    dropdown: {
      fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
      fontFamily: 'var(--YW_Theme_Font_Type)',
      borderRadius: '8px',
      border: 'none',
      selectors: {
        ':focus': {
          border: 'none',
          outline: 'none'
        },
        ':focus:after': {
          border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
          borderRadius: '8px'
        }
      }
    },
    dropdownItem: {
      fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
      fontFamily: 'var(--YW_Theme_Font_Type)',
      borderRadius: '8px',
      border: 'none',
      selectors: {
        '&:hover': {
          backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
          color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)'
        },
        '&:focus': {
          backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
          color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
          outline: 'none'
        },
        ':focus-within': {
          outline: 'none',
          border: 'none'
        }
      }
    },
    dropdownItemSelected: {
      fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
      fontFamily: 'var(--YW_Theme_Font_Type)',
      backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
      color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
      border: 'none',
      borderRadius: '8px',
      selectors: {
        '&:hover': {
          backgroundColor: 'var(--YW_Header_Program_Dropdown_Hover_Color)',
          color: 'var(--YW_Header_Program_Dropdown_Font_Hover_Color)',
          border: 'none'
        },
        '&:focus': {
          border: 'none',
          color: 'none'
        }
      }
    },
    title: {
      fontSize: 'var(--YW_SignIn_Modal_UserID_Text_Font_Size)',
      fontFamily: 'var(--YW_Theme_Font_Type)',
      border: 'none',
      borderRadius: '8px',
      selectors: {
        '&:focus': {
          outline: 'none !important',
          border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
          boxShadow: 'none !important'
        },
        '&:focus:after': {
          border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
          borderRadius: '8px'
        },
        '&:active': {
          outline: 'none !important',
          border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important',
          color: 'inherit !important'
        },
        '&:hover': {
          border: '1px solid var(--YW_SignIn_Modal_SwitchCase_Dropdown_Boundary_Color) !important'
        }
      }
    },
    caretDown: {
      color: 'var(--YW_SignIn_Modal_SwitchCase_Dropdown_Caret_Color)',
    },
  };


  const handleChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
    if (option) {
      onCaseChange(option.key as string);
    }
  };

  return (
    <Dropdown
      placeholder={casesString ? placeholder : 'No cases found.'}
      selectedKey={selectedCase}
      options={getCaseOptions(casesString, caseNumbersAndNamesVal)}
      onChange={handleChange}
      styles={dropdownStyles}
      responsiveMode={ResponsiveMode.large}
    />
  );
};

export default CaseDropdown;